<template>
  <div>
    <!-- ═══════════════════════════════════════════
         FILTER BAR
    ═══════════════════════════════════════════ -->
    <b-card class="mb-2">
      <b-card-text>
        <b-row class="align-items-end">
          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.from-date') || 'من تاريخ'"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.from_date"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.to-date') || 'الى تاريخ'"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.to_date"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="4"
            cols="12"
            class="d-flex align-items-end"
          >
            <div
              class="mb-2 d-flex"
              style="gap: 0.5rem"
            >
              <b-button
                variant="primary"
                :disabled="loading"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('common.search') || 'بحث' }}
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="loading"
                @click="resetFilters"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('common.reset') || 'إعادة تعيين' }}
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!-- Active date range pill -->
        <div
          v-if="activeDateLabel"
          class="active-filter-pill mt-2"
        >
          <feather-icon
            icon="CalendarIcon"
            size="12"
            class="mr-25"
          />
          {{ activeDateLabel }}
        </div>
      </b-card-text>
    </b-card>

    <!-- ═══════════════════════════════════════════
         DAILY BREAKDOWN TABLE
    ═══════════════════════════════════════════ -->
    <b-card>
      <b-card-text>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3 class="mb-0">
            {{ $t('accounting.reports-laboratory') || 'Laboratory Report' }}
          </h3>
          <div
            v-if="reportData && !loading"
            class="d-flex align-items-center"
          >
            <feather-icon
              icon="DollarSignIcon"
              size="16"
              class="text-success mr-50"
            />
            <span class="font-weight-bold text-success">
              {{ $t('common.total') || 'المجموع' }}:
              {{ formatIQDCurrency(reportData.total_money) }}
            </span>
          </div>
        </div>

        <b-table
          :items="dailyRows"
          :fields="tableColumns"
          :busy="loading"
          small
          responsive
          striped
          hover
          show-empty
          :empty-text="$t('common.no-data') || 'لا توجد بيانات'"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner
                small
                class="mr-50"
              />
              <span>{{ $t('common.loading') || 'جاري التحميل...' }}</span>
            </div>
          </template>

          <template #cell(invoice_count)="data">
            <b-badge
              variant="primary"
              pill
            >
              {{ data.item.invoice_count }}
            </b-badge>
          </template>

          <template #cell(total_amount)="data">
            <span class="font-weight-bold text-success">
              {{ formatIQDCurrency(data.item.total_amount) }}
            </span>
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="info"
              @click="openDetailsModal(data.item)"
            >
              <feather-icon
                icon="EyeIcon"
                class="mr-25"
              />
              {{ $t('common.details') || 'تفاصيل' }}
            </b-button>
          </template>
        </b-table>

        <small
          v-if="dailyRows.length"
          class="text-muted"
        >
          {{ $t('common.total') || 'المجموع' }}: <strong>{{ dailyRows.length }}</strong> {{ 'days' || 'أيام' }}
        </small>
      </b-card-text>
    </b-card>

    <!-- ═══════════════════════════════════════════
         DETAILS MODAL — invoices for a single day
    ═══════════════════════════════════════════ -->
    <b-modal
      v-model="showDetailsModal"
      size="xl"
      :title="selectedDay ? selectedDay.date : ($t('common.details') || 'تفاصيل')"
      hide-footer
      scrollable
    >
      <div v-if="selectedDay">
        <!-- day summary strip -->
        <b-row class="mb-3">
          <b-col
            md="4"
            cols="12"
            class="mb-2"
          >
            <b-card class="text-center">
              <feather-icon
                icon="CalendarIcon"
                size="24"
                class="text-primary mb-1"
              />
              <h5 class="mb-0">
                {{ selectedDay.date }}
              </h5>
              <small class="text-muted">{{ $t('common.date') || 'التاريخ' }}</small>
            </b-card>
          </b-col>
          <b-col
            md="4"
            cols="12"
            class="mb-2"
          >
            <b-card class="text-center">
              <feather-icon
                icon="ListIcon"
                size="24"
                class="text-info mb-1"
              />
              <h5 class="mb-0">
                {{ selectedDay.invoice_count }}
              </h5>
              <small class="text-muted">{{ $t('common.count') || 'عدد الفواتير' }}</small>
            </b-card>
          </b-col>
          <b-col
            md="4"
            cols="12"
            class="mb-2"
          >
            <b-card class="text-center">
              <feather-icon
                icon="DollarSignIcon"
                size="24"
                class="text-success mb-1"
              />
              <h5 class="mb-0">
                {{ formatIQDCurrency(selectedDay.total_amount) }}
              </h5>
              <small class="text-muted">{{ $t('common.total-amount') || 'إجمالي المبلغ' }}</small>
            </b-card>
          </b-col>
        </b-row>

        <!-- invoices table -->
        <h5 class="mb-2">
          <feather-icon
            icon="FileTextIcon"
            class="mr-50"
          />
          {{ $t('common.invoices') || 'الفواتير' }}
        </h5>

        <b-table
          :items="selectedDay.invoices || []"
          :fields="detailsColumns"
          small
          responsive
          striped
          hover
          show-empty
          :empty-text="$t('common.no-data') || 'لا توجد بيانات'"
        >
          <template #cell(invoice_no)="data">
            <span class="font-weight-bold text-nowrap">
              #{{ data.item.invoice_no }}
            </span>
          </template>

          <template #cell(amount)="data">
            <span class="font-weight-bold">
              {{ formatIQDCurrency(data.item.amount) }}
            </span>
          </template>

          <template #cell(discount)="data">
            <span class="text-warning">
              {{ formatIQDCurrency(data.item.discount || 0) }}
            </span>
          </template>

          <template #cell(net_amount)="data">
            <span class="font-weight-bold text-success">
              {{ formatIQDCurrency(data.item.net_amount || 0) }}
            </span>
          </template>

          <template #cell(paid_at)="data">
            <span class="text-nowrap">
              {{ formatDateTime(data.item.approved_at) }}
            </span>
          </template>
        </b-table>

        <div class="text-right mt-3">
          <b-button
            variant="secondary"
            @click="closeDetailsModal"
          >
            {{ $t('common.close') || 'إغلاق' }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BTable,
  BSpinner,
  BModal,
  BBadge,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BSpinner,
    BModal,
    BBadge,
    Datepicker,
  },
  data() {
    return {
      filters: {
        from_date: null,
        to_date: null,
      },
      loading: false,
      reportData: null,

      showDetailsModal: false,
      selectedDay: null,

      // Main table: one row per day
      tableColumns: [
        { key: 'date', label: this.$t('common.date') || 'التاريخ', sortable: true },
        { key: 'invoice_count', label: this.$t('common.count') || 'عدد الفواتير', sortable: true },
        { key: 'total_amount', label: this.$t('common.total-amount') || 'إجمالي المبلغ', sortable: true },
        { key: 'actions', label: this.$t('common.actions') || 'إجراءات' },
      ],

      // Details modal: one row per invoice
      detailsColumns: [
        { key: 'invoice_no', label: this.$t('common.invoice-number') || 'رقم الفاتورة' },
        { key: 'patient_name', label: this.$t('common.patient-name') || 'اسم المريض' },
        { key: 'amount', label: this.$t('common.amount') || 'المبلغ' },
        { key: 'discount', label: this.$t('common.discount') || 'الخصم' },
        { key: 'net_amount', label: this.$t('common.net-amount') || 'الصافي' },
        { key: 'paid_at', label: this.$t('common.timestamps') || 'وقت الدفع' },
      ],
    }
  },

  computed: {
    dailyRows() {
      if (!this.reportData || !Array.isArray(this.reportData.daily_breakdown)) return []
      return this.reportData.daily_breakdown
    },
    activeDateLabel() {
      const from = this.filters.from_date ? this.formatDateForQuery(this.filters.from_date) : null
      const to = this.filters.to_date ? this.formatDateForQuery(this.filters.to_date) : null
      if (from && to) return `${from}  →  ${to}`
      if (from) return `${this.$t('common.from-date') || 'من'}: ${from}`
      if (to) return `${this.$t('common.to-date') || 'إلى'}: ${to}`
      return null
    },
  },

  created() {
    this.getReportData()
  },

  methods: {
    applyFilters() {
      this.getReportData()
    },
    resetFilters() {
      this.filters.from_date = null
      this.filters.to_date = null
      this.getReportData()
    },
    buildDateParams() {
      const params = []
      if (this.filters.from_date) params.push(`from=${this.formatDateForQuery(this.filters.from_date)}`)
      if (this.filters.to_date) params.push(`to=${this.formatDateForQuery(this.filters.to_date)}`)
      return params.length ? `?${params.join('&')}` : ''
    },
    formatDateForQuery(val) {
      if (!val) return ''
      if (typeof val === 'string') return val
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    formatIQDCurrency(value) {
      const num = value === null || value === undefined ? 0 : Number(value)
      if (Number.isNaN(num)) return '0 IQD'
      return `${new Intl.NumberFormat('en-US').format(num)} IQD`
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      if (Number.isNaN(date.getTime())) return dateTime
      try {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
      } catch (e) { return dateTime }
    },
    openDetailsModal(dayRow) {
      this.selectedDay = dayRow
      this.showDetailsModal = true
    },
    closeDetailsModal() {
      this.showDetailsModal = false
      this.selectedDay = null
    },
    async getReportData() {
      this.loading = true
      try {
        const url = `/reports/laboratory${this.buildDateParams()}`
        const response = await this.$http.get(url)
        const payload = response?.data?.data

        if (!response?.data?.status) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              text:
                response?.data?.message
                || response?.data?.error
                || this.$t('common.error-fetching-data'),
              variant: 'danger',
            },
          })
        }

        this.reportData = payload || null
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            text:
              error?.response?.data?.message
              || error?.response?.data?.error
              || this.$t('common.error-fetching-data'),
            variant: 'danger',
          },
        })
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.active-filter-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
}
</style>
